import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { fmt } from "../../lib/money";
import { DollarSign, TrendingDown, Wallet, Search, X, Tag, ChevronDown, ChevronUp, Percent } from "lucide-react";

const STATUS_COLORS = {
  placed: "bg-[#0033FF] text-white",
  shipped: "bg-black text-white",
  delivered: "bg-[#00A355] text-white",
  cancelled: "bg-[#FF2A00] text-white",
};

const STATUS_FILTERS = [
  { k: "all", label: "All (excl. cancelled)" },
  { k: "placed", label: "Placed" },
  { k: "shipped", label: "Shipped" },
  { k: "delivered", label: "Delivered" },
  { k: "cancelled", label: "Cancelled" },
];

export default function SellerEarnings() {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try {
      const { data } = await api.get("/seller/earnings");
      setData(data);
    } catch {
      /* ignore */
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.orders;
    if (filter === "all") {
      rows = rows.filter((o) => o.status !== "cancelled");
    } else {
      rows = rows.filter((o) => o.status === filter);
    }
    if (q.trim()) {
      const ql = q.trim().toLowerCase();
      rows = rows.filter((o) =>
        o.order_id.toLowerCase().includes(ql) ||
        (o.customer_name || "").toLowerCase().includes(ql) ||
        o.items.some((it) => (it.title || "").toLowerCase().includes(ql))
      );
    }
    return rows;
  }, [data, filter, q]);

  if (!data) {
    return (
      <div className="text-sm text-neutral-500" data-testid="earnings-loading">
        Loading earnings…
      </div>
    );
  }

  const t = data.totals;
  const bs = t.by_status;

  return (
    <div data-testid="seller-earnings">
      <h1 className="font-display font-black text-4xl tracking-tight">Earnings</h1>
      <p className="mt-2 text-sm text-neutral-600">
        Track what you actually take home after affiliate commissions. Cancelled
        orders are excluded from your totals.
      </p>

      {/* Headline totals */}
      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Stat
          testid="earn-gross-all"
          icon={<DollarSign className="w-4 h-4" />}
          label="Sold for"
          value={fmt(t.gross_all)}
          accent={`Across ${(bs.placed.count + bs.shipped.count + bs.delivered.count)} active orders`}
        />
        <Stat
          testid="earn-discount-all"
          icon={<Percent className="w-4 h-4" />}
          label="Discounts given"
          value={fmt(t.discount_all || 0)}
          accent={t.discount_all > 0 ? "Codes redeemed by buyers" : "No discounts used yet"}
          tone="orange"
        />
        <Stat
          testid="earn-commission-all"
          icon={<TrendingDown className="w-4 h-4" />}
          label="Affiliate commission"
          value={fmt(t.commission_all)}
          accent="Total you've paid out"
          tone="red"
        />
        <Stat
          testid="earn-net-all"
          icon={<Wallet className="w-4 h-4" />}
          label="You collect"
          value={fmt(t.net_all)}
          accent={(t.commission_all + (t.discount_all || 0)) > 0
            ? `${fmt(t.gross_all)} − ${fmt((t.commission_all || 0) + (t.discount_all || 0))} deductions`
            : "Nothing deducted yet"}
          tone="green"
        />
      </div>

      {/* Breakdown by status */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
        {[
          { k: "placed", title: "Placed" },
          { k: "shipped", title: "Shipped" },
          { k: "delivered", title: "Delivered ✓" },
          { k: "cancelled", title: "Cancelled" },
        ].map((s) => {
          const r = bs[s.k];
          return (
            <div
              key={s.k}
              data-testid={`earn-bs-${s.k}`}
              className={`border ${s.k === "delivered" ? "border-[#00A355]" : "border-neutral-200"} p-3 min-w-0`}
            >
              <div className={`text-[10px] uppercase tracking-widest font-bold ${s.k === "delivered" ? "text-[#00A355]" : "text-neutral-500"}`}>
                {s.title}
              </div>
              <div className="font-display font-black text-lg mt-1 break-words">{fmt(r.net)}</div>
              <div className="text-[11px] text-neutral-500 mt-0.5 leading-snug">
                {r.count} {r.count === 1 ? "order" : "orders"}
                {r.discount > 0 && (
                  <span className="block text-[#FF9500]">
                    −{fmt(r.discount)} disc
                  </span>
                )}
                {r.commission > 0 && (
                  <span className="block text-[#FF2A00]">
                    −{fmt(r.commission)} comm
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="mt-8 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input
            data-testid="earnings-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search order, customer, or product…"
            className="w-full h-10 pl-10 pr-9 border border-black rounded-none text-sm"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-black"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.k}
              data-testid={`earnings-filter-${s.k}`}
              type="button"
              onClick={() => setFilter(s.k)}
              className={`h-10 px-3 text-xs uppercase tracking-widest font-bold border border-black transition-colors ${
                filter === s.k ? "bg-black text-white" : "bg-white text-black hover:bg-neutral-100"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Order list */}
      <div className="mt-4 border border-neutral-200">
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">
            No orders match.
          </div>
        ) : (
          filtered.map((o) => {
            const isOpen = openId === o.order_id;
            const hasComm = o.commission_paid > 0;
            const hasDisc = (o.discount_amount || 0) > 0;
            return (
              <div
                key={o.order_id}
                data-testid={`earn-order-${o.order_id}`}
                className="border-b border-neutral-200 last:border-b-0"
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : o.order_id)}
                  className="w-full text-left p-3 sm:p-4 hover:bg-neutral-50 transition-colors"
                >
                  <div className="grid grid-cols-[auto_1fr_auto] gap-3 items-start">
                    {isOpen ? <ChevronUp className="w-4 h-4 mt-1" /> : <ChevronDown className="w-4 h-4 mt-1" />}
                    <div className="min-w-0">
                      <div className="font-display font-bold text-sm truncate">
                        #{o.order_id.slice(-8)}
                        {o.tm_ref && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-[#0033FF] text-white px-1.5 py-0.5">
                            <Tag className="w-2.5 h-2.5" />affiliate
                          </span>
                        )}
                        {hasDisc && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold bg-[#FF9500] text-white px-1.5 py-0.5">
                            <Percent className="w-2.5 h-2.5" />discount
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {new Date(o.created_at).toLocaleDateString()} · {o.customer_name || "—"}
                      </div>
                      {(o.discount_codes || []).length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`earn-discount-codes-${o.order_id}`}>
                          {o.discount_codes.map((dc) => (
                            <span
                              key={dc.code}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FFE600] text-black border border-black"
                              title={`Code ${dc.code} — saved buyer ${fmt(dc.amount)}`}
                            >
                              <Tag className="w-2.5 h-2.5" />{dc.code}
                              <span className="font-normal normal-case tracking-normal text-neutral-700">−{fmt(dc.amount)}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="flex items-center justify-end gap-2 flex-wrap">
                        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 ${STATUS_COLORS[o.status]}`}>
                          {o.status}
                        </span>
                        <span className="font-display font-black text-lg text-[#00A355]" data-testid={`earn-net-${o.order_id}`}>
                          {fmt(o.net)}
                        </span>
                      </div>
                      <div className="text-[11px] text-neutral-500 mt-0.5 leading-tight">
                        Sold {fmt(o.gross)}
                        {hasDisc && (
                          <span className="block text-[#FF9500] font-bold">
                            −{fmt(o.discount_amount)} discount
                          </span>
                        )}
                        {hasComm && (
                          <span className="block text-[#FF2A00] font-bold">
                            −{fmt(o.commission_paid)} commission
                          </span>
                        )}
                        {!hasComm && !hasDisc && <span className="text-neutral-400"> · no deductions</span>}
                      </div>
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="px-3 sm:px-4 pb-4 pl-3 sm:pl-12 text-sm overflow-x-auto max-w-full" data-testid={`earn-detail-${o.order_id}`}>
                    <table className="w-full text-[11px] sm:text-[12px] min-w-[420px]">
                      <thead className="text-[10px] uppercase tracking-widest text-neutral-500">
                        <tr>
                          <th className="text-left py-1">Item</th>
                          <th className="text-right py-1">Qty</th>
                          <th className="text-right py-1">Sold for</th>
                          <th className="text-right py-1">Comm %</th>
                          <th className="text-right py-1">Comm</th>
                          <th className="text-right py-1">You earn</th>
                        </tr>
                      </thead>
                      <tbody>
                        {o.items.map((it) => (
                          <tr key={it.product_id} className="border-t border-neutral-100">
                            <td className="py-1.5 pr-2">{it.title}</td>
                            <td className="py-1.5 text-right">{it.qty}</td>
                            <td className="py-1.5 text-right">{fmt(it.gross)}</td>
                            <td className="py-1.5 text-right text-[#FF2A00]">{it.commission_pct ? `${it.commission_pct}%` : "—"}</td>
                            <td className="py-1.5 text-right text-[#FF2A00]">{it.commission > 0 ? `−${fmt(it.commission)}` : "—"}</td>
                            <td className="py-1.5 text-right text-[#00A355] font-bold">{fmt(it.net)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-neutral-200">
                          <td colSpan={2} className="py-1.5 text-neutral-500">Item subtotal</td>
                          <td className="py-1.5 text-right">{fmt(o.gross)}</td>
                          <td colSpan={2}></td>
                          <td className="py-1.5 text-right">{fmt(o.gross)}</td>
                        </tr>
                        {hasDisc && (o.discount_codes || []).map((dc) => (
                          <tr key={`disc-${dc.code}`} className="text-[#FF9500]">
                            <td colSpan={4} className="py-1.5">
                              Discount code <b>{dc.code}</b> ({dc.discount_type === "percent" ? `${o.gross > 0 ? Math.round(dc.amount / o.gross * 100) : 0}%` : "fixed"})
                            </td>
                            <td className="py-1.5 text-right">−{fmt(dc.amount)}</td>
                            <td className="py-1.5 text-right">−{fmt(dc.amount)}</td>
                          </tr>
                        ))}
                        {hasComm && (
                          <tr className="text-[#FF2A00]">
                            <td colSpan={4} className="py-1.5">Affiliate commission</td>
                            <td className="py-1.5 text-right">−{fmt(o.commission_paid)}</td>
                            <td className="py-1.5 text-right">−{fmt(o.commission_paid)}</td>
                          </tr>
                        )}
                        <tr className="border-t-2 border-black font-bold">
                          <td colSpan={5} className="py-1.5">You collect</td>
                          <td className="py-1.5 text-right text-[#00A355]">{fmt(o.net)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value, accent, tone, testid }) {
  const toneCls =
    tone === "green" ? "border-[#00A355] bg-[#F0FFF4]" :
    tone === "red" ? "border-[#FF2A00] bg-[#FFF5F2]" :
    tone === "orange" ? "border-[#FF9500] bg-[#FFF7EB]" :
    "border-neutral-200 bg-white";
  const valueCls =
    tone === "green" ? "text-[#00A355]" :
    tone === "red" ? "text-[#FF2A00]" :
    tone === "orange" ? "text-[#FF9500]" :
    "text-black";
  return (
    <div data-testid={testid} className={`border-2 p-3 sm:p-4 min-w-0 overflow-hidden ${toneCls}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold text-neutral-600 mb-2">
        {icon}
        <span className="break-words">{label}</span>
      </div>
      <div className={`text-xl sm:text-3xl font-display font-black leading-tight break-words ${valueCls}`}>
        {value}
      </div>
      {accent && <div className="text-[11px] text-neutral-500 mt-1 break-words leading-snug">{accent}</div>}
    </div>
  );
}
